<template>
  <div class="w-full my-4 px-4 revenueProjections flex flex-col justify-center">
    <div class="w-full h-full flex flex-row flex-wrap items-center justify-between revenueProjections__gapSm">
      <p class="text-base font-bold">Scheduled revenue</p>
      <div class="flex rounded timePeriod">
        <span
          v-for="(range, index) in ranges"
          :key="range.days"
          class="
            border-solid border-grey-200 border
            text-sm text-grey-500
            py-1
            px-2
            hover:text-pink
            cursor-pointer
            hover:border-pink
          "
          :class="{
            first: index === 0,
            third: index === ranges.length - 1,
            active: range.days === selectedDays,
          }"
          @click="selectedDays = range.days"
        >
          {{ range.label }}
        </span>
      </div>
    </div>

    <div v-if="loading" class="w-full">
      <chartContentLoader />
    </div>

    <div v-else-if="error" class="w-full h-80 flex flex-col justify-center items-center">
      <p class="text-sm text-center mb-2">Revenue projections could not be loaded.</p>
      <a href="#" class="text-sm text-pink font-bold" @click.prevent="load">Retry</a>
    </div>

    <div v-else-if="notUsed" class="w-full h-80 justify-center">
      <div class="w-full flex flex-col justify-center items-center h-full">
        <p class="text-xl font-bold mb-3">
          {{ $t('frontend_gd_dashboard_get_started_with') }} GrooveSell
        </p>
        <p class="text-sm w-1/2 text-center mb-4">
          {{ $t('frontend_gd_dashboard_get_no_data_to') }} GrooveSell
          {{ $t('frontend_gd_dashboard_get_today') }}.
        </p>
        <gd-button primary class="h-10 w-32" @click="$emit('redirectUrl', 'GrooveSell')">
          {{ $t('frontend_gd_dashboard_get_started') }}
        </gd-button>
      </div>
    </div>

    <div v-else-if="projections" class="w-full">
      <div class="w-full flex flex-row flex-wrap items-end justify-between mt-2 mb-2 revenueProjections__gapMd">
        <div class="flex flex-row revenueProjections__gapLg">
          <div class="flex flex-col">
            <p class="text-3xl text-color font-bold">{{ money(totals.scheduled) }}</p>
            <span class="text-sm text-gray-100">Scheduled</span>
          </div>
          <div class="flex flex-col">
            <p class="text-3xl text-color font-bold">{{ money(totals.expected) }}</p>
            <span class="text-sm text-gray-100">Expected</span>
          </div>
          <div class="flex flex-col justify-end">
            <span class="text-sm text-gray-100 mb-1">{{ chargesLabel }}</span>
          </div>
        </div>
        <div v-if="!isEmpty" class="flex rounded timePeriod">
          <span
            class="border-solid border-grey-200 border first text-sm text-grey-500 py-1 px-2 hover:text-pink cursor-pointer
              hover:border-pink"
            :class="{ active: metric === 'scheduled' }"
            @click="metric = 'scheduled'"
          >
            Scheduled
          </span>
          <span
            class="border-solid border-grey-200 border third text-sm text-grey-500 py-1 px-2 hover:text-pink cursor-pointer
              hover:border-pink"
            :class="{ active: metric === 'expected' }"
            @click="metric = 'expected'"
          >
            Expected
          </span>
        </div>
      </div>

      <div v-if="isEmpty" class="w-full h-64 flex flex-col justify-center items-center">
        <img src="../assets/images/icons/dashboard/emptyinsights.png" />
        <p class="text-sm w-1/2 text-center mb-4 mt-4">Nothing scheduled in this period.</p>
      </div>
      <vue-apex-charts
        v-else
        type="bar"
        height="300"
        :options="chartOptions"
        :series="chart.series"
        class="w-full"
      ></vue-apex-charts>

      <div v-if="!isEmpty && products.length" class="w-full mt-2">
        <p class="text-sm font-bold text-color mb-1">Top products</p>
        <div class="revenueProjections__products">
          <div class="revenueProjections__productRow revenueProjections__productHead text-xs text-gray-100">
            <span>Product</span>
            <span>Scheduled</span>
            <span>Expected</span>
          </div>
          <div
            v-for="product in products"
            :key="product.product_id"
            class="revenueProjections__productRow text-sm text-color"
          >
            <span class="revenueProjections__productName">{{ product.name }}</span>
            <span>{{ money(product.scheduled) }}</span>
            <span>{{ money(product.expected) }}</span>
          </div>
        </div>
      </div>

      <div class="w-full mt-3 revenueProjections__notes">
        <p>
          Expected uses your renewal history from the last 12 months<span v-if="rateLine">: {{ rateLine }}</span>.
        </p>
        <p v-if="otherCurrencies">Also scheduled in other currencies: {{ otherCurrencies }} (90 days).</p>
        <p v-if="pastDue > 0">
          {{ pastDue }} {{ pastDue === 1 ? 'subscription is' : 'subscriptions are' }} past due and not included.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import VueApexCharts from 'vue-apexcharts';
import chartContentLoader from '@/pages/members/chartcontentloader';
import {
  PROJECTION_RANGES,
  buildProjectionSeries,
  findWindow,
  formatMoney,
  isWindowEmpty,
  otherCurrenciesSummary,
  rateSummary,
  topProducts,
} from './revenueProjections';

export default {
  name: 'RevenueProjections',
  components: {
    VueApexCharts,
    chartContentLoader,
  },
  data() {
    return {
      ranges: PROJECTION_RANGES,
      selectedDays: 30,
      metric: 'scheduled',
      loading: false,
      error: false,
      projections: null,
    };
  },
  computed: {
    notUsed() {
      return !!this.projections && (this.projections.has_used === false || this.projections.has_used === 0);
    },
    currency() {
      return (this.projections && this.projections.currency) || 'USD';
    },
    activeWindow() {
      return findWindow(this.projections, this.selectedDays);
    },
    totals() {
      return (this.activeWindow && this.activeWindow.totals) || {};
    },
    chargesLabel() {
      const charges = Number(this.totals.charges) || 0;
      return `${charges.toLocaleString()} ${charges === 1 ? 'charge' : 'charges'}`;
    },
    isEmpty() {
      return isWindowEmpty(this.activeWindow);
    },
    chart() {
      return buildProjectionSeries(this.activeWindow, this.metric);
    },
    products() {
      return topProducts(this.activeWindow, 5);
    },
    rateLine() {
      return rateSummary(this.projections && this.projections.rates, this.activeWindow);
    },
    otherCurrencies() {
      return otherCurrenciesSummary(this.projections && this.projections.other_currencies);
    },
    pastDue() {
      return Number(this.projections && this.projections.past_due_subscriptions) || 0;
    },
    chartOptions() {
      const { currency } = this;
      return {
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        colors: this.chart.colors,
        dataLabels: { enabled: false },
        plotOptions: {
          bar: { horizontal: false, columnWidth: this.selectedDays === 30 ? '70%' : '55%' },
        },
        legend: { position: 'bottom', horizontalAlign: 'left', fontSize: '12px' },
        grid: {
          row: { colors: ['transparent', 'transparent'], opacity: 0.5 },
          strokeDashArray: 8,
        },
        xaxis: {
          categories: this.chart.categories,
          labels: { hideOverlappingLabels: true, rotate: -45, style: { fontSize: '11px' } },
          axisBorder: { show: false },
          tooltip: { enabled: false },
        },
        yaxis: {
          labels: { formatter: (value) => formatMoney(value, currency, true) },
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: { formatter: (value) => formatMoney(value, currency) },
        },
      };
    },
  },
  created() {
    this.load();
  },
  methods: {
    money(amount) {
      return formatMoney(amount, this.currency);
    },
    async load() {
      this.loading = true;
      this.error = false;
      try {
        const response = await axios.get('groovesell/charts/revenue-projections');
        this.projections = response.data || {};
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style>
.revenueProjections__gapSm {
  gap: 8px;
}
.revenueProjections__gapMd {
  gap: 16px;
}
.revenueProjections__gapLg {
  gap: 32px;
}
.revenueProjections__products {
  border-top: 1px solid #e5e9f0;
}
.revenueProjections__productRow {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(90px, auto) minmax(90px, auto);
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid #e5e9f0;
}
.revenueProjections__productRow > span:not(:first-child) {
  text-align: right;
}
.revenueProjections__productName {
  overflow-wrap: anywhere;
}
.revenueProjections__notes p {
  color: #5b6680;
  font-size: 12px;
  line-height: 1.5;
  margin: 0;
}
</style>
